<template>
  <UApp class="app-wrapper">
    <NuxtLayout>
      <NuxtPage @alertEmit="handleAlertEmit" />
    </NuxtLayout>

    <div class="alert-wrapper">
      <div v-for="(alert, index) in alerts" :key="index" class="alert-item">
        <v-alert
          density="compact"
          prominent
          :color="alert.color"
          :icon="alert.icon"
          :title="alert.title"
          :text="alert.text"
        />
      </div>
    </div>
  </UApp>
</template>

<script lang="ts" setup>
import { ref } from "vue";

// Reactive array for alerts
const alerts = ref<
  { color: string; icon: string; title: string; text: string }[]
>([]);

// Function to handle emitted alerts
const handleAlertEmit = (alertEmit: {
  color: string;
  icon: string;
  title: string;
  text: string;
}) => {
  // Add new alert to the stack
  alerts.value.push(alertEmit);

  // Remove the alert after 5 seconds
  setTimeout(() => {
    alerts.value.shift();
  }, 5000);
};
</script>

<style scoped>
.app-wrapper {
  position: relative;
  background: white;
  min-height: 100vh;
}

.alert-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.alert-item {
  animation: fade-in 0.3s ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

<style>
* {
  font-family: "Arial", sans-serif;
  margin: 0;
  padding: 0;
  box-sizing: border-box;

  /* Variables: colors */
  --dark-blue: #2c3e50;
  --white: #f6f6f6;
  --light-gray: #dbdbdb;
  --gray: #838383;

  --t-red: #dc3545;
  --t-red-2: #c0392b;
  --t-green: #28a745;
  --t-green-2: #27ae60;
  --t-yellow: #ffc107;
  --t-blue: #0c75e6;
  --t-blue-2: #3498db;
  --t-white: var(--white);

  --t-disabled-green: #8ac28c;
  --t-disabled-red: #ffcdd2;
  --t-disabled: #b1b1b1;

  /* Main Theme Colors */
  --dark-orange-1: #d54c00;
  --dark-orange-2: #e55e00;
  --dark-orange-3: #ef7817;
  --dark-orange-4: #ff8000;
  --dark-orange-5: #ff9100;

  --orange-1: #ff6e00;
  --orange-2: #ff7f00;
  --orange-3: #ff8f00;
  --orange-4: #ff9f00;
  --orange-4-darkened: #f59e13;
  --orange-5: #ffaf00;

  --yellow-orange-1: #ff9800;
  --yellow-orange-2: #ffa90f;
  --yellow-orange-3: #ffba1f;
  --yellow-orange-4: #ffcb2e;
  --yellow-orange-5: #ffdc3e;
  --yellow-orange-6: #fdf3e7;

  --transparent-orange-1: rgba(255, 110, 0, 0.1);
  --transparent-orange-2: rgba(255, 110, 0, 0.2);
  --transparent-orange-3: rgba(255, 110, 0, 0.3);
  --transparent-orange-4: rgba(255, 110, 0, 0.4);
  --transparent-orange-5: rgba(255, 110, 0, 0.5);

  --brown-1: #834211;
  --brown-2: #945320;
  --brown-3: #a5632f;
  --brown-4: #b6723e;
  --brown-5: #c7814d;

  --brown-grey-1: #54433a;
  --brown-grey-2: #65524a;
  --brown-grey-3: #756159;
  --brown-grey-4: #867068;
  --brown-grey-5: #967f78;

  --beige-grey-1: #d18152;
  --beige-grey-2: #d99263;
  --beige-grey-3: #e0a274;
  --beige-grey-4: #e7b385;
  --beige-grey-5: #efc496;

  --light-silver-1: #c2c9d0;
  --light-silver-2: #d8dfe6;
  --light-silver-3: #eef5fc;
  --light-silver-4: #f3f6fb;
  --light-silver-5: #f6f8ff;

  /* Theme Assignments - Using Modern Color System */
  --t-primary: var(--orange-700); /* Modern primary - darker orange */
  --t-primary-reducedopacity: var(--transparent-orange-1);
  --t-secondary: var(--brown-600); /* Modern secondary - modern brown */
  --t-tertiary: var(--brown-400); /* Modern tertiary - lighter brown */
  --t-accent: var(--primary-orange); /* Modern accent - main brand orange */

  --t-surface: var(--gray-200); /* Modern surface - light gray */

  /* Variables: color palette */
  /* --primary-color: var(--blue); */

  /* Size variables */
  --website-navbar-height: 87px;
  --app-navbar-height: 70px;
}

.spin-animation {
  animation: spin 1s linear infinite;
}

.accent-text {
  color: var(--t-accent);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
